import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';
import { useData } from '../../context/DataContext';
import EpubDevicePreview from './preview/EpubDevicePreview';
import PdfDevicePreview from './preview/PdfDevicePreview';

const {
  FiSmartphone, FiPrinter, FiDownload, FiRefreshCw,
  FiEye, FiAlertCircle, FiCheckCircle, FiHome, FiLock
} = FiIcons;

const NoPreviewBanner = ({ type, icon, downloadUrl, message, isTrial, isAdmin }) => (
  <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
    <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <SafeIcon icon={FiAlertCircle} className="text-2xl text-amber-500" />
    </div>
    <h4 className="text-base font-bold text-slate-700 mb-2">{type} Preview Unavailable</h4>
    <p className="text-sm text-slate-500 max-w-md mx-auto leading-relaxed mb-4">{message}</p>
    {downloadUrl && (!isTrial || isAdmin) && (
      <a
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-primary text-white font-bold text-sm"
      >
        <SafeIcon icon={FiDownload} />
        <span>Download {type} to Preview</span>
      </a>
    )}
  </div>
);

const PreviewStep = () => {
  const { pipelineState, setPipelineState, closeProject } = useFormat();
  const { currentUser } = useData();
  const [activeTab, setActiveTab] = useState(
    pipelineState?.previewData?.type === 'pdf' ? 'pdf' : 'epub'
  );

  const accessType = (currentUser?.access_type || 'trial').toLowerCase();
  const isAdmin = accessType === 'admin';
  const isTrial = accessType === 'trial';
  const isDownloadLocked = isTrial && !isAdmin;

  const { previewData, exportResult, exportType, allExports } = pipelineState || {};

  // Support both single and dual exports
  const epubResult = allExports?.epub || (exportType === 'epub' ? exportResult : null);
  const pdfResult = allExports?.pdf || (exportType === 'pdf' ? exportResult : null);
  const epubHtml = allExports?.epubHtml || previewData?.epubHtml || '';
  const pdfBase64 = allExports?.pdfBase64 || previewData?.pdfBase64 || '';

  const hasEpub = !!epubResult;
  const hasPdf = !!pdfResult;
  const hasEpubPreview = !!epubHtml;
  const hasPdfPreview = !!pdfBase64;

  const tabs = [];
  if (hasEpub || hasEpubPreview) tabs.push({ id: 'epub', label: 'E-Reader / EPUB', icon: FiSmartphone });
  if (hasPdf || hasPdfPreview) tabs.push({ id: 'pdf', label: 'Print / PDF', icon: FiPrinter });

  const handleConfirm = () => {
    setPipelineState(prev => ({ ...prev, step: 5, previewConfirmed: true }));
  };

  const handleRebuild = () => {
    setPipelineState(prev => ({
      ...prev,
      step: 5,
      exportResult: null,
      exportType: null,
      previewData: null,
      allExports: null,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto py-6 max-w-5xl"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
            <SafeIcon icon={FiCheckCircle} />
            <span>Preview Your Output</span>
          </div>
          <h2 className="text-2xl font-serif text-slate-800">
            Review Before Downloading
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Toggle between EPUB and PDF previews below.
          </p>
        </div>

        {/* Download buttons */}
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          <button
            onClick={handleRebuild}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-primary hover:border-primary/30 transition-all text-sm font-bold"
          >
            <SafeIcon icon={FiRefreshCw} />
            <span>Rebuild</span>
          </button>

          {isDownloadLocked ? (
            <div className="inline-flex items-center space-x-1 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-xs font-semibold">
              <SafeIcon icon={FiLock} />
              <span>Downloads Locked in Trial</span>
            </div>
          ) : (
            <>
              {epubResult?.download_url && (
                <a
                  href={epubResult.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-primary hover:bg-blue-800 shadow transition-all"
                >
                  <SafeIcon icon={FiSmartphone} />
                  <span>Download EPUB</span>
                </a>
              )}
              {pdfResult?.download_url && (
                <a
                  href={pdfResult.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-secondary hover:bg-orange-600 shadow transition-all"
                >
                  <SafeIcon icon={FiPrinter} />
                  <span>Download PDF</span>
                </a>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="flex space-x-1 bg-slate-100 p-1 rounded-2xl mb-6 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-bold uppercase tracking-widest transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <SafeIcon icon={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Preview area */}
      <AnimatePresence mode="wait">
        {activeTab === 'epub' && (
          <motion.div key="epub" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {hasEpubPreview ? (
              <EpubDevicePreview html={epubHtml} />
            ) : (
              <NoPreviewBanner
                type="EPUB"
                icon={FiSmartphone}
                downloadUrl={epubResult?.download_url}
                isTrial={isTrial}
                isAdmin={isAdmin}
                message="The EPUB HTML body was not returned by the server. Ensure build-epub.php returns body_html in its response."
              />
            )}
          </motion.div>
        )}
        {activeTab === 'pdf' && (
          <motion.div key="pdf" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {hasPdfPreview ? (
              <PdfDevicePreview pdfBase64={pdfBase64} downloadUrl={pdfResult?.download_url} />
            ) : (
              <NoPreviewBanner
                type="PDF"
                icon={FiPrinter}
                downloadUrl={pdfResult?.download_url}
                isTrial={isTrial}
                isAdmin={isAdmin}
                message="The PDF base64 was not returned by the server. Ensure build-pdf.php passes pdf_base64 from the VPS response to the frontend."
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-start space-x-3">
          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiEye} className="text-green-600" />
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">Happy with the output?</p>
            <p className="text-xs text-slate-500">Confirm to go to the finish screen, or rebuild with different settings.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={closeProject}
            className="flex items-center space-x-2 px-5 py-3 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-all text-sm font-bold"
          >
            <SafeIcon icon={FiHome} />
            <span>Dashboard</span>
          </button>
          <button
            onClick={handleConfirm}
            className="flex items-center space-x-2 px-7 py-3 rounded-xl bg-primary hover:bg-blue-800 text-white font-bold text-sm shadow-lg transition-all"
          >
            <SafeIcon icon={FiCheckCircle} />
            <span>Confirm &amp; Finish</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PreviewStep;